// @flow

import React, { Component } from 'react';
import { DropTarget } from 'react-dnd';
import i18n from 'app/common/lib/i18n';
import { compose } from 'redux';
import hoverable, { type hoverHandlersType } from 'app/common/lib/react/hoverable';
import { Box, Icon, IconButton, Image, Mask, Spinner, Text } from 'gestalt';

export type VideoType = {
  imageBase64?: string | ArrayBuffer,
  thumbnail?: string,
  uploadId: string,
};

type Props = {
  allowReset?: boolean,
  accepts: Array<string>,
  canDrop: boolean,
  connectDropTarget: Function,
  display: 'image' | 'pin',
  handleRemoveClick: () => void,
  hoverHandlers: hoverHandlersType,
  imagePreviewSrc: string,
  isHovering: boolean,
  isOver: boolean,
  isVideo: boolean,
  isVideoComplete: boolean,
  onVideoLoaded: ({ video: VideoType }) => {},
  videoId?: string,
  uploadId?: string,
  thumbnail?: string | ArrayBuffer,
  videoUrls: {
    hls?: string,
    '720p'?: string,
  },
  onClick: () => void,
  onDrop: () => void,
  height: number,
  width: number,
  progress: number,
};

const CAPTURE_DELAY = 500;

const boxTarget = {
  drop: (props, monitor) => {
    if (props.onDrop) {
      props.onDrop(props, monitor);
    }
  },
};

class DropTargetBox extends Component<Props> {
  get isPin() {
    const { display, imagePreviewSrc, videoUrls } = this.props;
    return display === 'pin' && (!!imagePreviewSrc || !!videoUrls);
  }

  // Hacky. Have to do this to avoid tainted canvas and to performantly capture thumbnail
  // If you know a better way, please improve this! Note: not currently used, just using
  // the default thumbnail image. Will have to switch to this for a custom thumbnail picker.
  attemptToAttachVideoTag = () => {
    if (this._videoContainerRef && this._videoContainerRef.childNodes[0]) {
      return;
    }
    const { videoUrls, width, height } = this.props;
    // $FlowFixMe: new errors found from flow 0.71 upgrade
    if (!videoUrls || !videoUrls.length) {
      return;
    }
    const video = document.createElement('video');
    const keys = Object.keys(videoUrls);
    for (let i = 0; i < keys.length; i += 1) {
      const source = document.createElement('source');
      source.type = keys[i] === 'hls' ? 'm3u8' : 'video/mp4';
      if (videoUrls[keys[i]]) {
        source.src = videoUrls[keys[i]];
      }
      video.appendChild(source);
    }
    video.addEventListener(
      'loadedmetadata',
      () => {
        setTimeout(() => this.captureFrame(video), CAPTURE_DELAY);
      },
      false,
    );
    video.height = height;
    video.setAttribute('crossOrigin', 'anonymous');
    video.width = width;
    video.loop = true;
    video.load();
    if (this._videoContainerRef) {
      this._videoContainerRef.appendChild(video);
    }
  };

  _videoContainerRef = null;

  removeVideoTag = () => {
    if (this._videoContainerRef && this._videoContainerRef.childNodes[0]) {
      this._videoContainerRef.removeChild(this._videoContainerRef.childNodes[0]);
    }
  };

  captureFrame = (video) => {
    const { onVideoLoaded, uploadId, videoId } = this.props;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    const imageBase64 = canvas.toDataURL();
    if (uploadId && videoId) {
      onVideoLoaded({
        video: {
          videoId,
          uploadId,
          thumbnail: imageBase64,
        },
      });
    }
  };

  clear = ({ event }: { event: SyntheticMouseEvent<> }) => {
    event.stopPropagation();

    this.removeVideoTag();
    this.props.handleRemoveClick();
  };

  renderProgressBar() {
    const { imagePreviewSrc, isVideo, progress } = this.props;
    const progressClass =
      progress === 100 || progress === 0 || (!imagePreviewSrc && !isVideo)
        ? 'uploadProgressBar transition'
        : 'uploadProgressBar';
    const trueProgress = imagePreviewSrc || isVideo ? progress : 0;
    return <div className={progressClass} style={{ width: `${trueProgress}%` }} />;
  }

  renderDropZone() {
    return (
      <Box>
        <Box justifyContent="center" alignItems="center" display="flex">
          <Box justifyContent="center" alignItems="center" display="flex">
            <Icon
              color="darkGray"
              icon="camera"
              accessibilityLabel={i18n._('Add a Photo', 'Add a photo as part of a form')}
              size={32}
            />
          </Box>
        </Box>
        <Box marginTop={2} paddingX={12}>
          <Text align="center" color="darkGray" weight="bold">
            {i18n._(
              'Drag and drop or click to upload',
              'Label on drag/drop area for uploading a pin',
            )}
          </Text>
        </Box>
      </Box>
    );
  }

  renderImage() {
    const { height, imagePreviewSrc, width } = this.props;
    const alt = i18n._('Your Upload', 'Image a pinner just uploaded to a form');
    return this.isPin ? (
      <img alt={alt} src={imagePreviewSrc} />
    ) : (
      <Image
        alt={alt}
        src={imagePreviewSrc}
        fit="cover"
        naturalHeight={height}
        naturalWidth={width}
      />
    );
  }

  renderResetButton() {
    return (
      <Box top left position="absolute" margin={2}>
        <IconButton
          accessibilityLabel={i18n._('Remove Photo', 'Remove photo from a form')}
          bgColor="lightGray"
          icon="clear"
          iconColor="darkGray"
          onClick={this.clear}
          size="sm"
        />
      </Box>
    );
  }

  renderContent() {
    const {
      height,
      imagePreviewSrc,
      isHovering,
      isVideo,
      isVideoComplete,
      isOver,
      thumbnail,
      videoUrls,
      allowReset = true,
      width,
    } = this.props;

    /* eslint-disable jsx-a11y/media-has-caption */
    const videoTag = videoUrls ? (
      <video
        height={this.isPin ? 'auto' : height}
        width={width}
        autoPlay
        controls
        loop
        poster={thumbnail || ''}
      >
        {Object.keys(videoUrls).map((key: string) => (
          <source src={videoUrls[key]} type={key === 'hls' ? 'm3u8' : 'video/mp4'} key={key} />
        ))}
      </video>
    ) : null;
    /* eslint-enable jsx-a11y/media-has-caption */

    const color = isOver ? 'lightGray' : 'white';

    return (
      <React.Fragment>
        <Mask shape="rounded" height="100%">
          <Box
            color={color}
            height="100%"
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            {!imagePreviewSrc && !isVideo && this.renderDropZone()}
            {isVideo &&
              !videoUrls && (
                <Box>
                  {isVideoComplete && (
                    <Text align="center" size="lg" weight="bold">
                      {i18n._(
                        'Processing Video',
                        'Loading message shown while user video is uploading',
                      )}
                    </Text>
                  )}
                  <Box paddingY={4}>
                    <Spinner
                      show
                      accessibilityLabel={i18n._(
                        'Video uploading',
                        'Loading spinner while video uploads',
                      )}
                    />
                  </Box>
                </Box>
              )}
            {videoTag}
            {imagePreviewSrc && this.renderImage()}
          </Box>
        </Mask>
        {(imagePreviewSrc || videoUrls) && allowReset && isHovering && this.renderResetButton()}
      </React.Fragment>
    );
  }

  render() {
    const {
      connectDropTarget,
      height,
      imagePreviewSrc,
      onClick,
      videoUrls,
      width,
      hoverHandlers: { onMouseOver, onMouseLeave },
    } = this.props;

    const mediaHeight = this.isPin ? 'auto' : height;
    const DROP_STYLE = {
      width,
      border: this.isPin ? 0 : '2px dashed #8e8e8e',
      borderRadius: '10px',
      cursor: imagePreviewSrc ? 'default' : 'pointer',
      height: mediaHeight,
      marginLeft: 'auto',
      marginRight: 'auto',
      outline: 'none',
      padding: 0,
    };
    const targetBox = (
      <Box position="relative">
        {this.renderProgressBar()}
        {!(imagePreviewSrc || videoUrls) ? (
          <button
            style={DROP_STYLE}
            onClick={onClick}
            onMouseEnter={onMouseOver}
            onMouseLeave={onMouseLeave}
          >
            {this.renderContent()}
          </button>
        ) : (
          <div onMouseEnter={onMouseOver} onMouseLeave={onMouseLeave} style={{ ...DROP_STYLE }}>
            {this.renderContent()}
          </div>
        )}
      </Box>
    );

    return connectDropTarget(<div>{targetBox}</div>);
  }
}

const dropCollect = (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  canDrop: monitor.canDrop(),
});

export default compose(
  hoverable,
  DropTarget((props) => props.accepts, boxTarget, dropCollect),
)(DropTargetBox);
